/**
 * buildNoteReviewData — the Prepare-a-Claim data assembler, extracted
 * VERBATIM from App.js fetchNoteForReview (Jun 2026, Prepare-Claim V2
 * migration Phase 2).  Single source of truth for BOTH hosts:
 *   • legacy App.js `fetchNoteForReview` wraps this and sets its state
 *   • PrepareClaimHostV2 wraps this for the native /v2/billing host
 * Returns the `noteReviewData` object; throws on failure.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export async function buildNoteReviewData(noteId, { findClientById } = {}) {
      const response = await axios.get(`${API}/notes/${noteId}`);
      const note = response.data;
      
      // Parse content if it's a JSON string
      if (note.content && typeof note.content === 'string') {
        try {
          note.content = JSON.parse(note.content);
        } catch (e) {
          void 0 && console.log("Note content is not JSON, keeping as string");
        }
      }
      
      // Fetch client data directly to ensure we always have fresh insurance data
      let reviewClient = null;
      if (note.client_id) {
        try {
          const clientResp = await axios.get(`${API}/clients/${note.client_id}`);
          reviewClient = clientResp.data;
        } catch (e) {
          console.warn('[fetchNoteForReview] Could not fetch client from API, falling back to cache:', e);
          reviewClient = typeof findClientById === 'function' ? findClientById(note.client_id) : null;
        }
      }
      
      // Get appointment if linked
      let appointment = null;
      if (note.appointment_id) {
        try {
          const aptResponse = await axios.get(`${API}/appointments/${note.appointment_id}`);
          appointment = aptResponse.data;
        } catch (error) {
          console.error("Failed to fetch appointment:", error);
        }
      }
      
      // Check for duplicate notes — enhanced with content, template, and CPT checks
      // Use summary=true so we don't drag 1000 notes worth of content blobs back
      // (full content for matches is fetched via GET /notes/{id} below).
      const duplicateCheckResponse = await axios.get(`${API}/notes?client_id=${note.client_id}&provider_id=${note.provider_id}&start_date=2020-01-01&limit=1000&summary=true`);
      const allClientNotes = duplicateCheckResponse.data || [];
      
      // Get current note's content fields for comparison
      const noteContent = note.content || {};
      const noteFields = noteContent.fields || (noteContent.sections || []).flatMap(s => s.fields || []);
      const noteFieldValues = noteFields
        .filter(f => f.value && f.type !== 'section_header' && f.type !== 'heading')
        .map(f => ({ id: f.id, value: JSON.stringify(f.value) }));
      const noteTemplate = note.template_id || note.template_name;
      const noteCpt = note.cpt_code || note._billing?.cpt_code || appointment?.cpt_code_id;
      
      const sameDateNotes = allClientNotes.filter(n => 
        n.id !== noteId && 
        n.date_of_service === note.date_of_service &&
        n.status !== 'draft'
      );
      
      // Build detailed duplicate warnings
      const duplicateWarnings = [];
      
      for (const dup of sameDateNotes) {
        const warnings = [];
        
        // (a) Same template + same content
        const dupTemplate = dup.template_id || dup.template_name;
        const isSameTemplate = dupTemplate && noteTemplate && dupTemplate === noteTemplate;
        
        if (isSameTemplate) {
          // Fetch full content for comparison
          try {
            const dupResp = await axios.get(`${API}/notes/${dup.id}`);
            const dupNote = dupResp.data;
            let dupContent = dupNote.content;
            if (typeof dupContent === 'string') {
              try { dupContent = JSON.parse(dupContent); } catch(e) { dupContent = {}; }
            }
            const dupFields = (dupContent?.fields || (dupContent?.sections || []).flatMap(s => s.fields || []))
              .filter(f => f.value && f.type !== 'section_header' && f.type !== 'heading')
              .map(f => ({ id: f.id, value: JSON.stringify(f.value) }));
            
            // Compare field values
            if (dupFields.length > 0 && noteFieldValues.length > 0) {
              const matchCount = noteFieldValues.filter(nf => 
                dupFields.some(df => df.id === nf.id && df.value === nf.value)
              ).length;
              const matchPct = Math.round((matchCount / Math.max(noteFieldValues.length, 1)) * 100);
              
              if (matchPct === 100) {
                warnings.push('Identical content — exact duplicate');
              } else if (matchPct >= 70) {
                warnings.push(`Very similar content (${matchPct}% match)`);
              }
            }
            
            if (warnings.length === 0 && isSameTemplate) {
              warnings.push('Same template on same date');
            }
          } catch (e) {
            if (isSameTemplate) warnings.push('Same template on same date');
          }
        }
        
        // (c) Same CPT on same DOS
        const dupCpt = dup.cpt_code || dup._billing?.cpt_code;
        if (noteCpt && dupCpt && noteCpt === dupCpt) {
          warnings.push(`Same CPT code (${dupCpt}) on same date — potential duplicate claim`);
        }
        
        // Check if a claim already exists for the duplicate note
        if (dup.status === 'billed' || dup.status === 'claim_created') {
          warnings.push('A claim has already been created from this note');
        }
        
        if (warnings.length === 0) {
          warnings.push('Same client and date of service');
        }
        
        duplicateWarnings.push({
          note: dup,
          warnings,
        });
      }
      
      // Also check for existing claims on same client + same DOS so the
      // biller can see them in the header — and one-click link the note
      // when there's an obvious match (typically due to the orphaned-note
      // bug where claim was created but note.claim_id wasn't written back).
      let existingClaimWarning = null;
      let existingClaims = [];
      try {
        const claimsResp = await axios.get(`${API}/claims/queue`);
        const allClaims = claimsResp.data?.claims || [];
        const sameDOSClaims = allClaims.filter(c =>
          c.client_id === note.client_id &&
          c.date_of_service === note.date_of_service &&
          !c.is_voided && !c.is_superseded &&
          c.status !== 'voided' && c.status !== 'deleted'
        );
        if (sameDOSClaims.length > 0) {
          existingClaimWarning = `${sameDOSClaims.length} existing claim(s) found for this client on ${note.date_of_service}`;
          existingClaims = sameDOSClaims.map(c => ({
            id: c.id,
            claim_number: c.claim_number,
            status: c.status,
            note_id: c.note_id,
            billed_amount: c.total_charged_amount || c.billed_amount,
          }));
        }
      } catch (e) {
        // Claims check is non-critical
      }
      
      // Fetch client's active diagnoses from the diagnoses collection
      let clientActiveDiagnoses = [];
      if (note.client_id) {
        try {
          const dxResp = await axios.get(`${API}/clients/${note.client_id}/diagnoses`);
          clientActiveDiagnoses = (dxResp.data || []).filter(d => d.is_active !== false);
        } catch (e) {
          console.warn('[fetchNoteForReview] Could not fetch client diagnoses:', e);
        }
      }

      // Jun 2026 — single source of truth for the service-line modifiers:
      // resolve them through the SAME endpoint that builds the created
      // claim (POST /claims/preview-from-note) so Prepare-a-Claim and the
      // claim can never disagree.  Non-critical: self-pay notes (or any
      // preview failure) fall back to the local derivation in the dialog.
      let previewModifiers = null;
      let previewServiceLocation = null;
      try {
        const modResp = await axios.post(`${API}/claims/preview-from-note/${noteId}`);
        const svcLines = modResp.data?.parsed?.service_lines || [];
        if (Array.isArray(svcLines[0]?.modifiers)) {
          previewModifiers = svcLines[0].modifiers;
        }
        // Jun 2026 — resolved service location (2310C / Box 32) so the
        // Prepare-a-Claim service line shows where the claim says the
        // service happened. Falls back to submitter (org) when no
        // location record resolved.
        const parsedPrev = modResp.data?.parsed || {};
        if (parsedPrev.facility) {
          previewServiceLocation = parsedPrev.facility;
        } else if (parsedPrev.submitter?.name) {
          previewServiceLocation = { ...parsedPrev.submitter, source: 'organization' };
        }
      } catch (e) {
        // fall back to local derivation
      }

      // Fallback: when the full 837 preview failed (e.g. 422 validation),
      // still resolve the service location via the dedicated endpoint so
      // billers always see where the claim will say the service happened.
      if (!previewServiceLocation) {
        try {
          const locResp = await axios.get(`${API}/notes/${noteId}/service-location`);
          if (locResp.data?.name) previewServiceLocation = locResp.data;
        } catch (e) {
          // non-critical
        }
      }


  return {
    note,
    appointment,
    potentialDuplicates: sameDateNotes,
    duplicateWarnings,
    existingClaimWarning,
    existingClaims,
    client: reviewClient,
    clientActiveDiagnoses,
    previewModifiers,
    previewServiceLocation,
  };
}
